import React from 'react';
import { theme } from '../../styles/theme';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  children,
  style,
  ...props
}) => {
  const baseStyles: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.weights.medium,
    border: 'none',
    borderRadius: theme.borderRadius.button,
    cursor: props.disabled ? 'not-allowed' : 'pointer',
    transition: `all ${theme.transitions.base}`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    width: fullWidth ? '100%' : 'auto',
    opacity: props.disabled ? 0.5 : 1,
    outline: 'none',
    position: 'relative',
    overflow: 'hidden'
  };

  const sizeStyles: Record<string, React.CSSProperties> = {
    sm: {
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      fontSize: theme.typography.sizes.sm,
      minHeight: '36px'
    },
    md: {
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      fontSize: theme.typography.sizes.base,
      minHeight: '44px'
    },
    lg: {
      padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
      fontSize: theme.typography.sizes.lg,
      minHeight: '52px'
    }
  };

  const variantStyles: Record<string, React.CSSProperties> = {
    primary: {
      backgroundColor: theme.colors.buttonPrimary || theme.colors.primary,
      color: '#FFFFFF',
      boxShadow: theme.shadows.md
    },
    secondary: {
      backgroundColor: theme.colors.buttonSecondary || theme.colors.surface,
      color: theme.colors.text.primary, // Use dark text instead of inverse
      border: `2px solid ${theme.colors.border}`,
      boxShadow: theme.shadows.sm
    },
    accent: {
      backgroundColor: theme.colors.buttonAccent || theme.colors.accent,
      color: theme.colors.text.inverse,
      boxShadow: theme.shadows.md
    },
    ghost: {
      backgroundColor: 'transparent',
      color: theme.colors.text.primary,
      border: `1px solid rgba(255, 255, 255, 0.3)`
    }
  };

  const hoverStyles: Record<string, React.CSSProperties> = {
    primary: {
      backgroundColor: '#374151', // Lighter dark slate
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows.lg
    },
    secondary: {
      backgroundColor: '#F9FAFB', // Very light gray
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows.md
    },
    accent: {
      backgroundColor: '#22C55E', // Brighter green
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows.lg
    },
    ghost: {
      backgroundColor: 'rgba(255, 255, 255, 0.1)'
    }
  };

  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <button
      {...props}
      onMouseEnter={(e) => {
        setIsHovered(true);
        props.onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        props.onMouseLeave?.(e);
      }}
      style={{
        ...baseStyles,
        ...sizeStyles[size],
        ...variantStyles[variant],
        ...(isHovered && !props.disabled ? hoverStyles[variant] : {}),
        ...style
      }}
    >
      {children}
    </button>
  );
};

export default Button;